// Redux Utils
import { useDispatch, useSelector } from "react-redux";
// React Utils
import React, { useState, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faMapMarkerAlt } from "@fortawesome/free-solid-svg-icons";

// Components
import Breadcrum from "@/components/Breadcrum";
import Modal from "@/components/Modals/Modal";
import DropdownMenu from "@/components/DropdownMenu";
import Tab from "@/components/Tab";
import { Link, useParams } from "react-router-dom";
// Components específicos de la vista
import BoxInfoEvento from "@/components/ViewEventoOwnerEmpresa/BoxInfoEvento";
import CardStatTipoTicket from "@/components/ViewEventoOwnerEmpresa/CardStatTipoTicket";
import DistribucionTicketsBar from "@/components/ViewEventoOwnerEmpresa/DistribucionTicketsBar";
import StatsNumericos from "@/components/ViewEventoOwnerEmpresa/StatsNumericos";
import DataTableClients from "@/components/ViewEventoOwnerEmpresa/DataTableClients";
import DataTableStaff from "@/components/ViewEventoOwnerEmpresa/DataTableStaff";
import DataTableTicketsVendidos from "@/components/ViewEventoOwnerEmpresa/DataTableTicketsVendidos";
import StatsGenerales from "@/components/ViewEventoOwnerEmpresa/StatsGenerales";
import CardInfoPuerta from "@/components/ViewEventoOwnerEmpresa/CardInfoPuerta";
import FormCrearPuerta from "@/components/ViewEventoOwnerEmpresa/formCrearPuerta";
import TabInvitacionesEvento from "@/components/ViewEventoOwnerEmpresa/TabInvitacionesEvento";
// Forms
import CancelarEventoPublicadoTerminos from "@/components/Forms/CancelarEventoPublicadoTerminos";
// Thunks
import { getPuertasPorEventoId } from "@/features/puerta/puertaThunks";
import {
  getStatsInfoEvento,
  getStatsTipoTickets,
  getStatsQuickStats,
  getStatsHistorialDeEntradas,
  getStatsAmbassadors,
  getStatsTicketsVendidos,
} from "@/features/evento/eventoThunks";
import { setPuertas, resetPuertas } from "@/features/puerta/puertaSlice";
import { getSolicitudesDeEvento } from "@/features/invitacion/invitacionThunks";

// Contextos
import { useModalsContext } from "@/contexts/ModalsContext";

// Tab info for horizontal layout
const HORIZONTAL_TAB_INFO = {
  title: "",
  description: "",
  layout: "horizontal",
  textColor: "text-white", // textColor de las tabs
  textColorHover: "hover:text-aw-green", // textColor al hacer hover en las tabs
  textColorActive: "text-aw-blueblack", // textColor de la tab activa
  bgColor: "bg-aw-bluedark-30", // bgColor del fondo de las tabs
  bgColorHover: "hover:bg-aw-bluedark-30", // bgColor al hacer hover en las tabs
  bgColorActive: "bg-aw-green", // bgColor de la tab activa
  borderColor: "border-aw-green",
  borderColorActive: "border-white",
  tabs: [
    {
      name: "Invitaciones",
      href: "#",
      current: true,
      icon: "fa-solid fa-envelope-open",
      contentClass: "py-6 sm:p-6",
    },
    {
      name: "Tickets Vendidos",
      href: "#",
      current: false,
      icon: "fa-solid fa-ticket",
      contentClass: "py-6 sm:p-6",
    },
    {
      name: "Accesos",
      href: "#",
      current: false,
      icon: "fa-solid fa-list-check",
      contentClass: "py-6 sm:p-6",
    },
    {
      name: "Staff",
      href: "#",
      current: false,
      icon: "fa-solid fa-users",
      contentClass: "py-6 sm:p-6",
    },
    // {
    //   name: "Estadísticas",
    //   href: "#",
    //   current: false,
    //   icon: "fa-solid fa-house",
    //   contentClass: "py-6 sm:p-6",
    // },
    {
      name: "Puertas",
      href: "#",
      current: false,
      icon: "fa-solid fa-door-open",
      contentClass: "py-6 sm:p-6",
    },
  ],
};

export default function ViewEventoOwnerEmpresa() {
  // Redux Utils
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.auth);
  const { puertas, isLoadingPuertas } = useSelector((state) => state.puerta);
  const {
    statsInfoEvento,
    statsTipoTickets,
    statsQuickStats,
    statsHistorialDeEntradas,
    statsTicketsVendidos,
    statsAmbassadors,
  } = useSelector((state) => state.evento);

  const { solicitudesEvento } = useSelector((state) => state.invitacion);

  const param = useParams();

  // Contextos
  const { modals, openModal, closeModal } = useModalsContext();

  // Transformar statsQuickStats en formato para StatsNumericos
  const statsData = React.useMemo(() => {
    if (!statsQuickStats) return [];

    return [
      {
        icon: "fa-users",
        label: "Accesos Totales",
        value: statsQuickStats.totalTicketsValidados || 0,
        change: null,
        iconBg: "bg-aw-green",
        iconColor: "text-aw-bluedark group-hover:animate-bounce",
        pillColor: "bg-red-200",
        gridClass:
          "col-span-12 md:col-start-1 md:col-end-4 md:row-start-1 md:row-end-2",
      },
      {
        icon: "fa-male",
        label: "Hombres",
        value: statsQuickStats.totalTicketsValidadosHombres || 0,
        change: null,
        iconBg: "bg-blue-400",
        iconColor: "text-aw-bluedark group-hover:animate-bounce",
        pillColor: "bg-green-200",
        gridClass:
          "col-span-12 md:col-start-4 md:col-end-7 md:row-start-1 md:row-end-2",
      },
      {
        icon: "fa-female",
        label: "Mujeres",
        value: statsQuickStats.totalTicketsValidadosMujeres || 0,
        change: null,
        iconBg: "bg-pink-400",
        iconColor: "text-aw-bluedark group-hover:animate-bounce",
        pillColor: "bg-gray-200",
        gridClass:
          "col-span-12 md:col-start-7 md:col-end-10 md:row-start-1 md:row-end-2",
      },
      {
        icon: "fa-genderless",
        label: "Otros",
        value: statsQuickStats.totalTicketsValidadosOtros || 0,
        change: null,
        iconBg: "bg-white",
        iconColor: "text-aw-bluedark group-hover:animate-spin",
        pillColor: "bg-yellow-200",
        gridClass:
          "col-span-12 md:col-start-10 md:col-end-13 md:row-start-1 md:row-end-2",
      },
      {
        icon: "fa-square-check",
        label: "Accesos Éxitosos",
        value: statsQuickStats.totalTicketsAccesosExitosos || 0,
        change: null,
        iconBg: "bg-teal-400",
        iconColor: "text-aw-bluedark group-hover:animate-bounce",
        pillColor: "bg-yellow-200",
        gridClass:
          "col-span-12 md:col-start-1 md:col-end-5 md:row-start-2 md:row-end-3",
      },
      {
        icon: "fa-users-slash",
        label: "Accesos Rechazados",
        value: statsQuickStats.totalTicketsAccesosRechazados || 0,
        change: null,
        iconBg: "bg-red-500",
        iconColor: "text-aw-bluedark group-hover:animate-bounce",
        pillColor: "bg-yellow-200",
        gridClass:
          "col-span-12 md:col-start-5 md:col-end-9 md:row-start-2 md:row-end-3",
      },
      {
        icon: "fa-handshake",
        label: "Porcentaje de Accesos vs. Tickets Vendidos",
        value: `${statsQuickStats.porcentajeDeAccesosExitososVsTicketsVendidos || 0}%`,
        change: null,
        iconBg: "bg-yellow-400",
        iconColor: "text-aw-bluedark group-hover:animate-bounce",
        pillColor: "bg-yellow-200",
        gridClass:
          "col-span-12 md:col-start-9 md:col-end-13 md:row-start-2 md:row-end-3",
      },
    ];
  }, [statsQuickStats]);

  // Estados
  const [visibleSection, setVisibleSection] = useState([
    { section: "quickStats", visible: true },
    { section: "handlers", visible: true },
    { section: "tickets", visible: true },
    { section: "dataGeneral", visible: true },
  ]);
  const [activeTab, setActiveTab] = useState(0);
  const [puertasLoaded, setPuertasLoaded] = useState(false);

  // TODO: Obtener estos valores de la URL o del estado de Redux
  const uidEvento = param.idEvento;
  const uidEmpresa = param.id; // Reemplazar con valor real

  // Efecto para cargar estadísticas del evento al montar el componente
  useEffect(() => {
    // Cargar todas las estadísticas del evento
    dispatch(getStatsInfoEvento({ uidEvento }));
    dispatch(getStatsTipoTickets({ uidEvento }));
    dispatch(getStatsQuickStats({ uidEvento }));
    dispatch(getStatsHistorialDeEntradas({ uidEvento, page: 1, limit: 50 }));
    dispatch(getStatsTicketsVendidos({ uidEvento, page: 1, limit: 50 }));
    dispatch(getStatsAmbassadors({ uidEvento, page: 1, limit: 50 }));
  }, [dispatch, uidEvento]);

  // Efecto para cargar puertas cuando se selecciona el tab de Puertas
  useEffect(() => {
    // La pestaña "Puertas" es el índice 4 (Invitaciones=0, Accesos=1, Tickets Vendidos=2, Staff=3, Puertas=4)
    if (activeTab === 4 && !puertasLoaded) {
      dispatch(getPuertasPorEventoId({ idEvento: uidEvento, uidEmpresa }))
        .unwrap()
        .then((data) => {
          if (data.puertas) {
            dispatch(setPuertas(data.puertas));
          }
          setPuertasLoaded(true);
        })
        .catch((error) => {
          console.error("Error al cargar puertas:", error);
        });
    }
    // La pestaña "Invitaciones" es el índice 0
    if (activeTab === 0) {
      dispatch(getSolicitudesDeEvento({ uidEvento }));
    }
  }, [activeTab, puertasLoaded, dispatch, uidEvento, uidEmpresa]);

  // Cleanup al desmontar componente
  useEffect(() => {
    return () => {
      dispatch(resetPuertas());
    };
  }, [dispatch]);

  // Links para el breadcrumb
  const breadcrumbLinks = [
    { label: "Inicio", href: "/" },
    {
      label: `${statsInfoEvento?.empresaNombre || "..."}`,
      href: `/empresa/${uidEmpresa}`,
    },
    { label: `${statsInfoEvento?.nombre || "..."}`, href: "#" },
  ];
  // Manejador de visibilidad de secciones
  const handlerVisibleSection = (section) => {
    setVisibleSection((prev) =>
      prev.map((p) =>
        p.section === section ? { ...p, visible: !p.visible } : p,
      ),
    );
  };

  // Componente de contenido para Puertas
  const PuertasContent = () => (
    <div className="space-y-6">
      {/* Header con botón crear */}
      <div className="flex justify-between items-center">
        <div>
          <h3 className="text-xl font-bold text-white">
            Dispositivos Validadores
          </h3>
          <p className="text-sm text-gray-400 mt-1">
            Gestiona los dispositivos que validarán tickets en tu evento
          </p>
        </div>
        {statsInfoEvento?.fechaHoraFinEvento && new Date(statsInfoEvento.fechaHoraFinEvento.replace(" ", "T") + "Z") > new Date() ? (
          <button
            onClick={() => openModal("crearPuerta")}
            className="px-4 py-2 font-semibold bg-aw-green text-aw-bluedark rounded-md hover:scale-95 transition-transform flex items-center gap-2"
          >
            <FontAwesomeIcon icon={"fa-solid fa-door-open"} />
            Crear Puerta
          </button>
        ) : (
          <></>
        )}
      </div>

      {/* Lista de puertas */}
      {isLoadingPuertas && (
        <div className="flex justify-center items-center py-12">
          <svg
            className="animate-spin h-8 w-8 text-aw-green"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            ></circle>
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            ></path>
          </svg>
        </div>
      )}

      {!isLoadingPuertas && puertas.length === 0 && (
        <div className="text-center py-12">
          <i className="fa-solid fa-door-open text-6xl text-gray-600 mb-4"></i>
          <p className="text-gray-400 text-lg">
            No hay puertas validadoras creadas
          </p>
          <p className="text-gray-500 text-sm mt-2">
            Crea tu primera puerta para empezar a validar tickets
          </p>
        </div>
      )}

      {!isLoadingPuertas && puertas.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {puertas.map((puerta) => (
            <CardInfoPuerta
              key={puerta.uid}
              puerta={puerta}
              uidEmpresa={uidEmpresa}
              fechaHoraFinEvento={statsInfoEvento?.fechaHoraFinEvento}
              zonaHoraria={statsInfoEvento?.zonaHoraria}
            />
          ))}
        </div>
      )}
    </div>
  );

  // Tabs horizontal
  const horizontalContent = [
    <TabInvitacionesEvento
      key="invitaciones"
      solicitudes={solicitudesEvento}
      uidEvento={uidEvento}
    />,
    <DataTableTicketsVendidos key="tickets-vendidos" statsTicketsVendidos={statsTicketsVendidos} uidEvento={uidEvento} />,
    <DataTableClients
      key="historial"
      statsHistorialDeEntradas={statsHistorialDeEntradas}
    />,
    <DataTableStaff key="staff" statsAmbassadors={statsAmbassadors} />,
    // <StatsGenerales key="estadisticas" />,
    <PuertasContent key="puertas" />,
  ];

  return (
    <>
      {/* Breadcrumbs */}
      <div className="w-full flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 pt-4 px-2 font-bold">
        <Breadcrum links={breadcrumbLinks} />
        <span className="text-aw-green">
          <Link
            to={`/empresa/${uidEmpresa}`}
            className="group rounded-2xl bg-aw-bluedark-50 hover:bg-aw-bluedark-85 hover:pl-3 duration-300 p-2 flex items-center"
          >
            <FontAwesomeIcon
              icon="fa-solid fa-arrow-left"
              className="mr-2 group-hover:mr-5 duration-300"
            />
            Volver
          </Link>
        </span>
      </div>
      {/* Resumen de la Información del Evento */}
      <div className="w-full flex flex-col items-center justify-between mb-5 px-2">
        {/* INIT BoxInfoEvento - Información del Evento */}
        <BoxInfoEvento
          uidEvento={uidEvento}
          uidEmpresa={uidEmpresa}
          nombre={statsInfoEvento?.nombre}
          fechaHoraInicioEvento={statsInfoEvento?.fechaHoraInicioEvento}
          fechaHoraFinEvento={statsInfoEvento?.fechaHoraFinEvento}
          zonaHoraria={statsInfoEvento?.zonaHoraria}
          published={statsInfoEvento?.published}
          descripcion={statsInfoEvento?.descripcion}
          ubicacion={statsInfoEvento?.ubicacion}
          direccion={statsInfoEvento?.direccion}
          categoriaEvento={statsInfoEvento?.categoriaEvento}
          aforo={statsInfoEvento?.aforo}
          totalTicketsPuestosEnVenta={
            statsInfoEvento?.totalTicketsPuestosEnVenta
          }
          totalTicketsVendidos={statsInfoEvento?.totalTicketsVendidos}
          imgEvento={statsInfoEvento?.imgEvento || null}
        />
        {/* END BoxInfoEvento - Información del Evento */}

        {/* Distribución de Tickets */}
        <div className="w-full mx-auto mb-5 bg-aw-bluedark-75 rounded-xl border border-gray-600 py-6 px-8">
          <DistribucionTicketsBar
            icon="fa-solid fa-ticket"
            title="Tickets Vendidos"
            date={statsInfoEvento?.fechaHoraInicioEvento || ""}
            total={statsInfoEvento?.totalTicketsVendidos || 0}
            data={[
              {
                label: "Hombres",
                value: statsInfoEvento?.totalTicketsCompradasPorHombres || 0,
                color: "#3b82f6",
              },
              {
                label: "Mujeres",
                value: statsInfoEvento?.totalTicketsCompradasPorMujeres || 0,
                color: "#e60076",
              },
              {
                label: "Otros",
                value: statsInfoEvento?.totalTicketsCompradasPorOtros || 0,
                color: "#FFFFFF",
              },
            ]}
          />
        </div>

        {/* Tipos de Tickets del Evento */}
        <div className="w-full">
          <h3 className="text-xl text-left font-semibold mb-3 text-white">
            Tipos de Tickets
          </h3>
          {/* Grid de 1 en móviles y de 4 en pantallas grandes de los tipos de tickets que hay en este evento */}
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-4">
            {statsTipoTickets && statsTipoTickets.length > 0 ? (
              statsTipoTickets.map((ticket) => (
                <CardStatTipoTicket
                  key={ticket.id}
                  ticketTypeName={ticket.ticketTypeName}
                  imgEvento={ticket.imgEvento}
                  imgTypeTicket={ticket.imgTypeTicket}
                  precioVenta={ticket.precioVenta}
                  totalTicketsPuestosEnVenta={ticket.totalTicketsPuestosEnVenta}
                  totalDeTicketsComprados={ticket.totalDeTicketsComprados}
                  totalTicketsCompradosPorHombres={
                    ticket.totalTicketsCompradosPorHombres || 0
                  }
                  totalTicketsCompradosPorMujeres={
                    ticket.totalTicketsCompradosPorMujeres || 0
                  }
                  totalTicketsCompradosPorOtros={
                    ticket.totalTicketsCompradosPorOtros || 0
                  }
                />
              ))
            ) : (
              <p className="text-gray-400 col-span-full text-center py-8">
                Cargando tipos de tickets...
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Stats numéricos */}
      <div className="w-full flex flex-col justify-between mb-8 px-2 gap-x-4">
        <h3 className="text-xl text-left font-semibold mb-3 text-white">
          Estadísticas Rápidas de Entradas
          <FontAwesomeIcon
            onClick={() => handlerVisibleSection("quickStats")}
            icon={`fa-solid ${
              visibleSection.find((s) => s.section === "quickStats")?.visible
                ? "fa-eye"
                : "fa-eye-slash"
            }`}
            className="ml-3 text-aw-green cursor-pointer"
          />
        </h3>
        <div
          className={`w-full ${
            visibleSection.find((s) => s.section === "quickStats")?.visible
              ? "block"
              : "hidden"
          }`}
        >
          <StatsNumericos statsData={statsData} />
        </div>
      </div>
      {/* Tab de Datos Generales */}
      <div className="w-full flex flex-col justify-between mb-10 px-2 gap-x-4">
        <h3 className="text-xl text-left font-semibold mb-3 text-white">
          Datos Generales
          <FontAwesomeIcon
            onClick={() => handlerVisibleSection("dataGeneral")}
            icon={`fa-solid ${
              visibleSection.find((s) => s.section === "dataGeneral")?.visible
                ? "fa-eye"
                : "fa-eye-slash"
            }`}
            className="ml-3 text-aw-green cursor-pointer"
          />
        </h3>
        <div
          className={`w-full ${
            visibleSection.find((s) => s.section === "dataGeneral")?.visible
              ? "block"
              : "hidden"
          }`}
        >
          <Tab
            tabInfo={HORIZONTAL_TAB_INFO}
            onTabChange={(index) => setActiveTab(index)}
          >
            {horizontalContent}
          </Tab>
        </div>
      </div>
      {/* Modals Section */}
      {/* Modal Cancelar Evento */}
      {modals.cancelar && (
        <Modal
          isOpen={modals.cancelar}
          onClose={() => closeModal("cancelar")}
          large="max-w-4xl"
        >
          <CancelarEventoPublicadoTerminos />
        </Modal>
      )}

      {/* Modal Crear Puerta */}
      {modals.crearPuerta && (
        <Modal
          isOpen={modals.crearPuerta}
          onClose={() => closeModal("crearPuerta")}
          large="max-w-2xl"
        >
          <FormCrearPuerta
            onClose={() => closeModal("crearPuerta")}
            uidEvento={uidEvento}
            uidEmpresa={uidEmpresa}
            eventStart={statsInfoEvento?.fechaHoraInicioEvento}
            timezone={statsInfoEvento?.zonaHoraria}
          />
        </Modal>
      )}
    </>
  );
}
